/**
 * Shared Header Component
 *
 * Reusable header with navigation, auth state, and mobile menu.
 */

import { Link, useNavigate } from '@tanstack/react-router'
import { Building2, ChevronDown, Heart, LogOut, Menu, Search, Settings, Sparkles, User, X } from 'lucide-react'
import { useState } from 'react'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Sheet, SheetClose, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { useAuthStore } from '@/stores/auth-store'

const NAV_LINKS = [
	{ to: '/search', label: 'Köpa företag' },
	{ to: '/register', label: 'Sälja företag' },
	{ to: '/about', label: 'Om oss' },
] as const

const ACCOUNT_LINKS = [
	{ to: '/account', label: 'Mitt konto', icon: User },
	{ to: '/account/shortlist', label: 'Favoriter', icon: Heart },
	{ to: '/account/saved-searches', label: 'Sparade sökningar', icon: Search },
	{ to: '/account/notifications', label: 'Inställningar', icon: Settings },
] as const

export function Header() {
	const { user, isAuthenticated, logout } = useAuthStore()
	const navigate = useNavigate()
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

	async function handleLogout() {
		await logout()
		navigate({ to: '/' })
	}

	function closeMobileMenu() {
		setMobileMenuOpen(false)
	}

	return (
		<header className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
			<div className="container mx-auto flex h-16 items-center justify-between px-4">
				{/* Logo */}
				<Link to="/" className="flex items-center gap-2">
					<Building2 className="h-7 w-7 text-primary" />
					<span className="text-xl font-bold text-foreground">Företagsbörsen</span>
				</Link>

				{/* Desktop Navigation */}
				<nav className="hidden items-center gap-6 md:flex">
					{NAV_LINKS.map(link => (
						<Link key={link.to} to={link.to} className="text-muted-foreground transition-colors hover:text-foreground">
							{link.label}
						</Link>
					))}
					{isAuthenticated && (
						<Link
							to="/swipe"
							className="inline-flex items-center gap-1.5 text-primary font-medium transition-colors hover:text-primary/80"
						>
							<Sparkles className="h-4 w-4" />
							Swipa
						</Link>
					)}
				</nav>

				{/* Desktop Auth Section */}
				<div className="hidden items-center gap-3 md:flex">
					{isAuthenticated ? (
						<ProfileDropdown user={user} onLogout={handleLogout} />
					) : (
						<>
							<Button variant="ghost" asChild>
								<Link to="/login">Logga in</Link>
							</Button>
							<Button asChild>
								<Link to="/register">Registrera</Link>
							</Button>
						</>
					)}
				</div>

				{/* Mobile Menu Trigger */}
				<Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
					<SheetTrigger asChild className="md:hidden">
						<Button variant="ghost" size="icon">
							<Menu className="h-6 w-6" />
							<span className="sr-only">Öppna meny</span>
						</Button>
					</SheetTrigger>
					<SheetContent side="right" className="w-[300px] sm:w-[350px]">
						<SheetHeader>
							<SheetTitle className="flex items-center gap-2">
								<Building2 className="h-5 w-5 text-primary" />
								Företagsbörsen
							</SheetTitle>
						</SheetHeader>

						<div className="mt-6 flex flex-col gap-4">
							{/* Mobile Navigation Links */}
							<nav className="flex flex-col gap-2">
								{NAV_LINKS.map(link => (
									<SheetClose key={link.to} asChild>
										<Link
											to={link.to}
											className="flex items-center rounded-md px-3 py-2 text-base font-medium text-foreground transition-colors hover:bg-accent"
											onClick={closeMobileMenu}
										>
											{link.label}
										</Link>
									</SheetClose>
								))}
							</nav>

							{/* Divider */}
							<div className="border-t border-border" />

							{/* Mobile Auth Section */}
							{isAuthenticated ? (
								<>
									{/* User Info */}
									<div className="flex items-center gap-3 px-3 py-2">
										<Avatar className="h-10 w-10">
											<AvatarFallback className="bg-primary/10 text-primary">
												{user?.name?.[0]?.toUpperCase() || 'U'}
											</AvatarFallback>
										</Avatar>
										<div className="flex flex-col">
											<span className="font-medium">{user?.name || 'Användare'}</span>
											<span className="text-sm text-muted-foreground">{user?.email}</span>
										</div>
									</div>

									{/* Swipe Feature Link */}
									<SheetClose asChild>
										<Link
											to="/swipe"
											className="flex items-center gap-3 rounded-md bg-primary/5 px-3 py-3 text-base font-medium text-primary transition-colors hover:bg-primary/10"
											onClick={closeMobileMenu}
										>
											<Sparkles className="h-5 w-5" />
											Swipa företag
										</Link>
									</SheetClose>

									{/* Account Links */}
									<nav className="flex flex-col gap-1">
										{ACCOUNT_LINKS.map(link => (
											<SheetClose key={link.to} asChild>
												<Link
													to={link.to}
													className="flex items-center gap-3 rounded-md px-3 py-2 text-base font-medium text-foreground transition-colors hover:bg-accent"
													onClick={closeMobileMenu}
												>
													<link.icon className="h-5 w-5 text-muted-foreground" />
													{link.label}
												</Link>
											</SheetClose>
										))}
									</nav>

									{/* Logout Button */}
									<Button
										variant="ghost"
										className="justify-start gap-3 text-destructive hover:bg-destructive/10 hover:text-destructive"
										onClick={() => {
											handleLogout()
											closeMobileMenu()
										}}
									>
										<LogOut className="h-5 w-5" />
										Logga ut
									</Button>
								</>
							) : (
								<div className="flex flex-col gap-2">
									<SheetClose asChild>
										<Button asChild className="w-full">
											<Link to="/login" onClick={closeMobileMenu}>
												Logga in
											</Link>
										</Button>
									</SheetClose>
									<SheetClose asChild>
										<Button variant="outline" asChild className="w-full">
											<Link to="/register" onClick={closeMobileMenu}>
												Skapa konto
											</Link>
										</Button>
									</SheetClose>
								</div>
							)}
						</div>
					</SheetContent>
				</Sheet>
			</div>
		</header>
	)
}

interface ProfileDropdownProps {
	user: { name?: string; email?: string } | null
	onLogout: () => void
}

function ProfileDropdown({ user, onLogout }: ProfileDropdownProps) {
	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button variant="ghost" className="flex items-center gap-2">
					<Avatar className="h-8 w-8">
						<AvatarFallback className="bg-primary/10 text-primary">
							{user?.name?.[0]?.toUpperCase() || 'U'}
						</AvatarFallback>
					</Avatar>
					<span className="hidden lg:inline">{user?.name || 'Användare'}</span>
					<ChevronDown className="h-4 w-4" />
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end" className="w-56">
				<div className="px-2 py-1.5">
					<p className="text-sm font-medium">{user?.name || 'Användare'}</p>
					<p className="text-xs text-muted-foreground">{user?.email}</p>
				</div>
				<DropdownMenuSeparator />
				{ACCOUNT_LINKS.map(link => (
					<DropdownMenuItem key={link.to} asChild>
						<Link to={link.to} className="flex items-center gap-2">
							<link.icon className="h-4 w-4" />
							{link.label}
						</Link>
					</DropdownMenuItem>
				))}
				<DropdownMenuSeparator />
				<DropdownMenuItem
					className="text-destructive focus:bg-destructive/10 focus:text-destructive"
					onClick={onLogout}
				>
					<LogOut className="mr-2 h-4 w-4" />
					Logga ut
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	)
}

export default Header
